import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Check } from "lucide-react";
import Logo from "@/components/Logo";

const LOOKING = ["Novas amizades", "Companhia para fazer planos", "Conhecer pessoas com interesses semelhantes", "Experimentar coisas novas", "Aumentar o meu círculo social", "Conhecer melhor o Porto", "Outro"];
const PLAN_TYPES = ["Dinners & drinks", "Brunch & coffee dates", "Culture & exhibitions", "Concerts & music", "Wellness & self-care", "Sports & outdoors", "Workshops & creative experiences", "Travel & weekend escapes"];
const SCENARIO = [
  ["A", "Ia apresentar-me e começar conversa."],
  ["B", "Provavelmente esperava pelo momento certo, mas tentava aproximar-me."],
  ["C", "Esperava que alguém viesse falar comigo."],
  ["D", "Depende completamente do contexto."],
];
const FRIDAY = [
  ["A", "Jantar num sítio novo"],
  ["B", "Vinho e boa conversa"],
  ["C", "Concerto, exposição ou qualquer coisa a acontecer na cidade"],
  ["D", "Pilates amanhã de manhã, por isso hoje fico por casa"],
  ["E", "Surpreendam-me. Eu alinho."],
];

export default function Apply() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    name: "", birth_date: "", city: "", profession: "", instagram: "", email: "", phone: "", password: "",
    q1_brought: "", q2_looking_for: [], q3_plan_types: [], q4_scenario: "", q5_good_friend: "", q6_contribution: "", q7_friday: "",
    agree_etiquette: false, agree_terms: false, agree_privacy: false, marketing_optin: false,
  });

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleArr = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agree_etiquette || !f.agree_terms || !f.agree_privacy) {
      toast.error("Tens de aceitar os valores, os Termos e a Política de Privacidade.");
      return;
    }
    setLoading(true);
    try {
      const phoneDigits = f.phone.replace(/\D/g, "");
      if (!/^9\d{8}$/.test(phoneDigits)) {
        toast.error("Número de telemóvel inválido. Usa um número português com 9 dígitos (ex: 912 345 678).");
        setLoading(false);
        return;
      }
      const { agree_etiquette, agree_terms, agree_privacy, ...rest } = f;
      const payload = { ...rest, phone: `+351 ${phoneDigits}` };
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      toast.success("Candidatura recebida ✦ Falamos em breve.");
      navigate("/area");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFCFA]">
      <header className="border-b border-[#E3DED2]">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center"><Logo variant="dark" className="h-9" /></Link>
          <Link to="/" className="inline-flex items-center gap-2 sc-label text-[#6E5F58] hover:text-[#48020C]"><ArrowLeft className="w-4 h-4" /> Início</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 sm:px-10 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="sc-label text-[#48020C]">✦ Apply to The Social Club</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#26130E] mt-4 leading-tight">This could be the start of something good.</h1>
          <p className="text-[#6E6359] mt-5 leading-relaxed">
            Queremos conhecer-te um bocadinho melhor.
            <br /><br />
            O The Social Club é uma comunidade com curadoria, para um número limitado de membros, criada para aproximar mulheres que procuram novas ligações, bons planos e pessoas com quem partilhá-los.
            <br /><br />
            Todas as candidaturas são revistas antes da entrada no Club. Não há respostas certas, queremos apenas perceber quem és e se o The Social Club faz sentido para ti.
          </p>
        </motion.div>

        <form onSubmit={submit} data-testid="apply-form" className="mt-14 space-y-12">
          {/* 01 */}
          <Section n="01" title="About you">
            <div className="grid sm:grid-cols-2 gap-6">
              <F label="Nome e apelido"><Input data-testid="apply-name" required value={f.name} onChange={set("name")} className="sc-in" placeholder="O teu nome" /></F>
              <F label="Data de nascimento"><Input data-testid="apply-birthdate" type="date" required max="2026-12-31" value={f.birth_date} onChange={set("birth_date")} className="sc-in" /></F>
              <F label="Cidade onde vives"><Input data-testid="apply-city" required value={f.city} onChange={set("city")} className="sc-in" placeholder="Porto" /></F>
              <F label="Profissão / área profissional"><Input data-testid="apply-profession" required value={f.profession} onChange={set("profession")} className="sc-in" placeholder="Ex: Arquiteta" /></F>
              <F label="Instagram — @username"><Input data-testid="apply-instagram" value={f.instagram} onChange={set("instagram")} className="sc-in" placeholder="@username" /></F>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <F label="Email"><Input data-testid="apply-email" type="email" required value={f.email} onChange={set("email")} className="sc-in" placeholder="email@exemplo.com" /></F>
              <F label="Telemóvel">
                <div className="flex items-center gap-2">
                  <span className="sc-in flex items-center text-[#6E6359] shrink-0" style={{ paddingTop: 0, paddingBottom: 0, height: "auto" }}>+351</span>
                  <Input data-testid="apply-phone" type="tel" required inputMode="numeric" maxLength={11} value={f.phone} onChange={set("phone")} className="sc-in" placeholder="912 345 678" />
                </div>
              </F>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <F label="Password"><Input data-testid="apply-password" type="password" required minLength={6} value={f.password} onChange={set("password")} className="sc-in" placeholder="Mínimo 6 caracteres" /></F>
            </div>
          </Section>

          {/* 02 */}
          <Section n="02" title="Let's get to know you">
            <F label="1. O que te trouxe até ao The Social Club?">
              <Textarea data-testid="apply-q1" required value={f.q1_brought} onChange={set("q1_brought")} className="sc-in min-h-24" placeholder="Resposta aberta curta" />
            </F>
            <MultiF label="2. O que gostarias de encontrar nesta comunidade? (podes escolher várias)" options={LOOKING} selected={f.q2_looking_for} onToggle={(v) => toggleArr("q2_looking_for", v)} testid="q2" />
            <MultiF label="3. Que tipo de planos têm mais a tua cara? (podes escolher vários)" options={PLAN_TYPES} selected={f.q3_plan_types} onToggle={(v) => toggleArr("q3_plan_types", v)} testid="q3" />
            <ChoiceF label="4. Imagina que entras no Club sem conhecer ninguém. Vês outra rapariga que também parece estar sozinha. O que farias?" options={SCENARIO} value={f.q4_scenario} onSelect={(v) => setF({ ...f, q4_scenario: v })} testid="q4" />
            <F label="5. Para ti, o que faz de alguém uma boa amiga?">
              <Textarea data-testid="apply-q5" value={f.q5_good_friend} onChange={set("q5_good_friend")} className="sc-in min-h-24" placeholder="Resposta aberta curta" />
            </F>
            <F label="6. E tu, o que achas que podes trazer ao The Social Club?">
              <Textarea data-testid="apply-q6" value={f.q6_contribution} onChange={set("q6_contribution")} className="sc-in min-h-24" placeholder="Resposta aberta curta" />
            </F>
          </Section>

          {/* 03 */}
          <Section n="03" title="Now for the fun part.">
            <ChoiceF label="7. É sexta-feira à noite. Qual é o plano? 🍸" options={FRIDAY} value={f.q7_friday} onSelect={(v) => setF({ ...f, q7_friday: v })} testid="q7" />
          </Section>

          {/* Consents */}
          <div className="space-y-4 border-t border-[#E3DED2] pt-8">
            <Consent testid="agree-etiquette" checked={f.agree_etiquette} onChange={(v) => setF({ ...f, agree_etiquette: v })}>
              Concordo com os valores e regras de convivência do The Social Club (<Link to="/club-etiquette" target="_blank" className="underline underline-offset-2 text-[#48020C]">Club Etiquette</Link>).*
            </Consent>
            <Consent testid="agree-terms" checked={f.agree_terms} onChange={(v) => setF({ ...f, agree_terms: v })}>
              Li e aceito os <Link to="/termos" target="_blank" className="underline underline-offset-2 text-[#48020C]">Termos de Utilização</Link>.*
            </Consent>
            <Consent testid="agree-privacy" checked={f.agree_privacy} onChange={(v) => setF({ ...f, agree_privacy: v })}>
              Li a <Link to="/privacidade" target="_blank" className="underline underline-offset-2 text-[#48020C]">Política de Privacidade</Link> e compreendi o tratamento dos meus dados.*
            </Consent>
            <Consent testid="marketing-optin" checked={f.marketing_optin} onChange={(v) => setF({ ...f, marketing_optin: v })}>
              Quero receber por email novidades, próximos eventos e outras comunicações do The Social Club. (opcional)
            </Consent>
          </div>

          <button type="submit" disabled={loading} data-testid="apply-submit" className="w-full bg-[#48020C] hover:bg-[#26130E] disabled:opacity-60 text-[#FCFCFA] font-medium rounded-full py-4 transition-colors">
            {loading ? "A enviar…" : "Enviar Candidatura →"}
          </button>
        </form>
      </main>
      <style>{`.sc-in{background:#F4F1EA;border-color:#E3DED2;border-radius:0.75rem;height:auto;padding:0.7rem 1rem;}`}</style>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-6">
        <span className="font-serif text-2xl text-[#48020C]">{n}</span>
        <h2 className="font-serif text-3xl text-[#26130E]">{title}</h2>
      </div>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function F({ label, children }) {
  return <div className="space-y-2"><Label className="text-sm font-medium text-[#3A1F19]">{label}</Label>{children}</div>;
}

function MultiF({ label, options, selected, onToggle, testid }) {
  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-[#3A1F19]">{label}</Label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button type="button" key={o} data-testid={`${testid}-${o}`} onClick={() => onToggle(o)}
              className={`text-sm rounded-full px-4 py-2 border transition-colors ${on ? "bg-[#48020C] text-[#FCFCFA] border-[#48020C]" : "bg-transparent text-[#6E6359] border-[#E3DED2] hover:border-[#48020C]"}`}>
              {on && <Check className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}{o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChoiceF({ label, options, value, onSelect, testid }) {
  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-[#3A1F19]">{label}</Label>
      <div className="space-y-2">
        {options.map(([k, text]) => {
          const on = value === k;
          return (
            <button type="button" key={k} data-testid={`${testid}-${k}`} onClick={() => onSelect(k)}
              className={`w-full text-left flex items-center gap-3 rounded-xl px-4 py-3 border transition-colors ${on ? "bg-[#F7EEC7] border-[#48020C] text-[#26130E]" : "bg-[#F4F1EA] border-[#E3DED2] text-[#3A1F19] hover:border-[#48020C]"}`}>
              <span className={`font-serif text-lg w-6 ${on ? "text-[#48020C]" : "text-[#6E5F58]"}`}>{k}</span>
              <span className="text-sm">{text}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Consent({ testid, checked, onChange, children }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <Checkbox data-testid={testid} checked={checked} onCheckedChange={onChange} className="mt-1 border-[#48020C] data-[state=checked]:bg-[#48020C] data-[state=checked]:border-[#48020C]" />
      <span className="text-sm text-[#3A1F19] leading-relaxed">{children}</span>
    </label>
  );
}
